/**
 * Pure helpers for BasicButton / BaseButtonMixin styling (runtime + designer).
 * No Vue instance or utils.compile — safe for designer/canvas code paths.
 */

export const BUTTON_BORDER_CLASS = 'c-baseButton--bordered';
export const ICON_PULSE_CLASS = 'c-btn-icon--pulse';
/** Visual border chrome around standalone Icon (not an MDI glyph). */
export const ICON_CHROME_BORDER_CLASS = 'c-Icon--bordered';

/**
 * Normalize Icon / BasicButton Animation field to Spin | Pulse | None.
 * @param {unknown} raw
 * @returns {'Spin'|'Pulse'|'None'}
 */
export function normalizeIconAnimation(raw) {
    if (raw == null || raw === '') return 'None';
    const s = String(raw).trim();
    if (/^spin$/i.test(s)) return 'Spin';
    if (/^pulse$/i.test(s)) return 'Pulse';
    return 'None';
}

/**
 * Normalize icon value for Quasar QIcon.
 * Legacy webfont form "mdi mdi-account" becomes "mdi-account" for all icon styles.
 * When iconStyle is "mdi" (or unset), bare names like "account" become "mdi-account".
 * @param {unknown} iconValue
 * @param {{ iconStyle?: string }} [options]
 * @returns {unknown}
 */
export function normalizeIconName(iconValue, options = {}) {
    if (iconValue == null || iconValue === '')
        return iconValue;

    let icon = String(iconValue).trim();
    if (!icon)
        return icon;

    icon = icon.replace(/^\s*mdi\s+/i, '').trim();

    const iconStyle = options.iconStyle;
    const useMdiPrefix = iconStyle == null || iconStyle === '' || iconStyle === 'mdi';

    if (useMdiPrefix && !icon.startsWith('mdi-') && !icon.includes(':'))
        icon = `mdi-${icon}`;

    return icon;
}

/**
 * Raw placement string from controlData (TooltipPlacement or TooltipLocation).
 * Designer: pass literal; runtime: pass evaluated string.
 * @param {{ TooltipPlacement?: unknown, TooltipLocation?: unknown }|null|undefined} controlData
 * @returns {string}
 */
export function iconTooltipPlacementRaw(controlData) {
    const raw = controlData?.TooltipPlacement ?? controlData?.TooltipLocation;
    return normalizeTooltipPlacementKey(raw);
}

/**
 * Normalize schema / designer placement strings to map keys (lowercase, hyphenated).
 * @param {string|null|undefined} location
 * @returns {string}
 */
export function normalizeTooltipPlacementKey(location) {
    if (location == null || location === '') return 'top';
    let s = String(location).trim().toLowerCase();
    // Unicode hyphen, underscores, runs of spaces → single ASCII hyphen
    s = s.replace(/\u2010|\u2011|\u2012|\u2013|\u2014|\u2212/g, '-');
    s = s.replace(/_/g, '-');
    s = s.replace(/\s+/g, '-');
    s = s.replace(/-+/g, '-');

    const oneWordAliases = {
        topleft: 'top-left',
        topright: 'top-right',
        bottomleft: 'bottom-left',
        bottomright: 'bottom-right',
        lefttop: 'left-top',
        leftbottom: 'left-bottom',
        righttop: 'right-top',
        rightbottom: 'right-bottom',
    };
    if (oneWordAliases[s]) return oneWordAliases[s];

    return s;
}

/**
 * Map ControlData TooltipLocation / TooltipPlacement to Quasar QTooltip anchor/self
 * (each value is "<vertical> <horizontal>" per Quasar position-engine).
 * @param {string|null|undefined} location
 * @returns {{ anchor: string, self: string }}
 */
export function tooltipLocationToAnchorSelf(location) {
    const loc = normalizeTooltipPlacementKey(location);

    const map = {
        top: { anchor: 'top middle', self: 'bottom middle' },
        'top-left': { anchor: 'top start', self: 'bottom start' },
        'top-right': { anchor: 'top end', self: 'bottom end' },
        bottom: { anchor: 'bottom middle', self: 'top middle' },
        'bottom-left': { anchor: 'bottom start', self: 'top start' },
        'bottom-right': { anchor: 'bottom end', self: 'top end' },
        left: { anchor: 'center left', self: 'center right' },
        'left-top': { anchor: 'top start', self: 'top end' },
        'left-bottom': { anchor: 'bottom start', self: 'bottom end' },
        right: { anchor: 'center right', self: 'center left' },
        'right-top': { anchor: 'top end', self: 'top start' },
        'right-bottom': { anchor: 'bottom end', self: 'bottom start' },
    };

    if (map[loc]) return map[loc];

    // Legacy single-word placements used by utils.generateTooltip / callers (not in BasicButton schema)
    if (loc === 'end' || loc === 'right') return map.right;
    if (loc === 'start' || loc === 'left') return map.left;
    if (loc === 'auto') return map.top;

    return map.top;
}

/**
 * Gentler vertical offset for "above" tooltips so viewport boundary logic flips less often.
 * @param {string|null|undefined} location
 * @returns {[number, number]|undefined} pass to QTooltip `offset` when defined
 */
export function tooltipOffsetForPlacement(location) {
    const loc = normalizeTooltipPlacementKey(location);
    if (loc === 'top' || loc.startsWith('top-')) return [0, 8];
    return undefined;
}

/**
 * Class map for QIcon: MDI flip/spin helpers + app pulse class (MDI has no pulse animation).
 * @param {{ flipH?: boolean, flipV?: boolean, animation?: string }} opts
 * @returns {Record<string, boolean>}
 */
export function iconAffixClasses({ flipH, flipV, animation }) {
    const classes = {};
    const anim = animation == null ? 'None' : String(animation);

    if (flipH) classes['mdi-flip-h'] = true;
    if (flipV) classes['mdi-flip-v'] = true;
    if (anim === 'Spin') classes['mdi-spin'] = true;
    else if (anim === 'Pulse') classes[ICON_PULSE_CLASS] = true;

    return classes;
}

/**
 * @param {unknown} resolvedBorder — runtime-evaluated boolean or designer literal
 */
export function buttonHasExplicitBorder(resolvedBorder) {
    return resolvedBorder === true || resolvedBorder === 'true';
}

/**
 * Normalize BasicButton `Color` after evaluation: strip optional `outline-` prefix,
 * lowercase, and map `default` / empty to `secondary`.
 * @param {unknown} evaluatedRaw — value from controlData.Color (literal or evaluated)
 * @returns {string} base token (e.g. primary, success, light), never outline-prefixed
 */
export function schemaButtonColorBaseTokenFromEvaluatedRaw(evaluatedRaw) {
    let t =
        evaluatedRaw == null || evaluatedRaw === ''
            ? 'secondary'
            : String(evaluatedRaw).trim().toLowerCase();
    if (t.startsWith('outline-')) t = t.slice('outline-'.length);
    if (t === '') t = 'secondary';
    if (t === 'default') t = 'secondary';
    return t;
}

/**
 * Map JSON schema / legacy Vuetify-style button color tokens to Quasar `QBtn` `color`
 * names that have matching `.bg-*` / `.text-*` utilities in Quasar’s palette.
 *
 * Accepts literal or evaluated `Color` (including `outline-*`); the outline prefix is
 * stripped via {@link schemaButtonColorBaseTokenFromEvaluatedRaw}.
 *
 * Schema set: primary, outline-*, secondary, success, info, warning, danger, link,
 * outline-secondary, outline-success, outline-danger, outline-warning, outline-info,
 * outline-light, outline-dark.
 *
 * @param {unknown} evaluatedRawOrBase
 * @returns {string}
 */
export function mapSchemaButtonColorToQuasar(evaluatedRawOrBase) {
    const t = schemaButtonColorBaseTokenFromEvaluatedRaw(evaluatedRawOrBase);
    switch (t) {
        case 'success':
            return 'positive';
        case 'danger':
            return 'negative';
        case 'link':
            return 'primary';
        case 'light':
            return 'grey-5';
        case 'dark':
            return 'dark';
        default:
            return t;
    }
}

/** Opt-in sentence-case chrome (see App.vue). */
export const SENTENCE_CASE_BUTTON_CLASS = 'c-SentenceCaseButton';
/** Opt-out of sentence case when ControlData.UseLiteralCasing is true. */
export const LITERAL_CASING_BUTTON_CLASS = 'c-useLiteralCasing';

/**
 * Treat schema / evaluated button flags as boolean (true / "true").
 * @param {unknown} value
 * @returns {boolean}
 */
export function coerceSchemaBoolean(value) {
    return value === true || value === 'true';
}

/**
 * QBtn classes matching BasicButton Color=outline-primary (see BasicButton.css).
 * @param {{ useLiteralCasing?: boolean }} [options]
 * @returns {string}
 */
export function outlinePrimaryQBtnClassNames(options = {}) {
    const classes = ['c-BasicButton--outline-primary', 'q-my-xs'];
    if (!options.useLiteralCasing)
        classes.push(SENTENCE_CASE_BUTTON_CLASS);
    return classes.join(' ');
}

/** @deprecated Prefer {@link outlinePrimaryQBtnClassNames}; kept for callers that always sentence-case. */
export const OUTLINE_PRIMARY_QBTN_CLASS_NAMES = outlinePrimaryQBtnClassNames();
